import { useState } from "react";
import { X, ZoomIn } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useInView } from "@/hooks/useInView";
import { galleryImages } from "@/data/gallery";

const CATEGORY_KEYS = ["all", "accommodations", "nature", "location"];

// AS TUAS NOVAS FOTOS
const NEW_PHOTOS = [
  { src: "/nazare 1.jpg", alt: "Nazaré", category: "location" },
  { src: "/nazaré 2.jpg", alt: "Nazaré Canhão", category: "location" },
  { src: "/paredes.jpg", alt: "Praia das Paredes", category: "location" },
  { src: "/Alcobaça-mosteiro.jpg", alt: "Mosteiro de Alcobaça", category: "location" },
  { src: "/alcobaça doces.jpg", alt: "Doces de Alcobaça", category: "location" },
  { src: "/glamping.jpg", alt: "Best Glamping Portugal", category: "nature" },
  { src: "/d83d6aef-bee2-405e-9578-c0e210c9244c.jpg", alt: "Quinta das Cantigas", category: "nature" }
];

export default function Gallery() {
  const { t } = useLanguage();
  const [ref, inView] = useInView();
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  // JUNÇÃO SIMPLES: Mantém as originais e acrescenta as novas
  const allImages = [...galleryImages, ...NEW_PHOTOS];

  const filtered =
    activeCategory === "all"
      ? allImages
      : allImages.filter((img) => img.category === activeCategory);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#F3F4F1]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#3A4D39] font-manrope">
              {t("gallery.badge")}
            </span>
            <div className="flex-1 h-px bg-[#3A4D39]/20" />
          </div>
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-normal text-[#1F2937] leading-tight">
                {t("gallery.title")}
              </h2>
              <p className="text-base font-manrope font-light text-[#6B7280] mt-3 max-w-lg">
                {t("gallery.subtitle")}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {CATEGORY_KEYS.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`rounded-full px-4 py-2 text-sm font-manrope font-medium transition-all duration-200 ${
                    activeCategory === cat
                      ? "bg-[#3A4D39] text-white shadow-sm"
                      : "bg-white text-[#6B7280] hover:bg-stone-200 border border-stone-200"
                  }`}
                >
                  {t(`gallery.categories.${cat}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-0">
            {filtered.map((img, i) => (
              <div
                key={`${img.src}-${i}`}
                className="break-inside-avoid mb-4 group relative overflow-hidden rounded-xl cursor-pointer"
                onClick={() => setLightbox(img)}
              >
                <img
                  src={img.src}
                  alt={img.alt || "Gallery Image"}
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </section>
  );
}
